import { dmDisplayName } from './dm-display';
import type { TeamChatLiveAlert } from './live-alert-store';
import type { Channel } from './queries';

export interface TeamChatWidgetAlert extends TeamChatLiveAlert {
  source: 'realtime' | 'unread';
  unreadVersion?: string;
}

function unreadVersion(channel: Channel): string {
  return [
    channel.last_message_at ?? '',
    channel.last_message_sender ?? '',
    channel.last_message_preview ?? '',
    channel.unread_count ?? 0,
  ].join('|');
}

/**
 * Merge realtime cards with the server's unread channel snapshots. The latter
 * makes the widget recover after reload/reconnect instead of only showing
 * messages that happened while this browser tab was already connected.
 */
export function buildTeamChatWidgetAlerts(
  liveAlerts: TeamChatLiveAlert[],
  channels: Channel[],
  currentUserId: string | null,
  dismissedUnreadVersions: Record<string, string>,
): TeamChatWidgetAlert[] {
  const channelById = new Map(channels.map((channel) => [channel.id, channel]));
  const liveChannelIds = new Set(liveAlerts.map((alert) => alert.channelId));

  const realtime: TeamChatWidgetAlert[] = liveAlerts.map((alert) => {
    const channel = channelById.get(alert.channelId);
    return {
      ...alert,
      messageCount: Math.max(alert.messageCount, channel?.unread_count ?? 0),
      source: 'realtime',
      unreadVersion: channel ? unreadVersion(channel) : undefined,
    };
  });

  const unread: TeamChatWidgetAlert[] = channels
    .filter(
      (channel) =>
        channel.type !== 'voice' &&
        (channel.unread_count ?? 0) > 0 &&
        !liveChannelIds.has(channel.id),
    )
    .flatMap((channel) => {
      const version = unreadVersion(channel);
      const dismissed = dismissedUnreadVersions[channel.id];
      if (dismissed === '*' || dismissed === version) return [];

      const isDirect = channel.type === 'direct';
      const channelName = isDirect
        ? dmDisplayName(channel, currentUserId,undefined,'')
        : channel.name || '';
      const senderName = channel.last_message_sender || channelName;

      return [
        {
          eventId: `unread:${channel.id}:${version}`,
          channelId: channel.id,
          channelName,
          isDirect,
          senderName,
          preview: channel.last_message_preview || '',
          ...(!channel.last_message_preview?{previewCopy:{kind:'pending' as const}}:{}),
          createdAt: channel.last_message_at ?? '',
          messageCount: channel.unread_count ?? 1,
          source: 'unread' as const,
          unreadVersion: version,
        },
      ];
    });

  return [...realtime, ...unread].sort((a, b) => {
    const aTime = Date.parse(a.createdAt) || 0;
    const bTime = Date.parse(b.createdAt) || 0;
    return bTime - aTime;
  });
}
